/**
 * lib/receiptPdf.ts
 * ------------------------------------------------------------------
 * A4 payment-receipt PDF in the finance house style (mirrors
 * invoicePdf.ts / statementPdf.ts: jsPDF, navy letterhead, SCHOOL
 * config, fmtPula). Produced on demand from a financePayments record
 * so a parent can be given/sent proof of payment.
 *
 *   buildReceiptPdf(payment, opt).save('receipt_RCP-2026-0042.pdf')
 *   buildReceiptPdf(payment, opt).output('blob')  // upload / email
 * ---------------------------------------------------------------- */
import { jsPDF } from 'jspdf';
import { SCHOOL } from '@/lib/school.config';
import { fmtPula, PAYMENT_METHOD_LABELS, type Payment } from '@/lib/financeCore';

export interface ReceiptPdfOptions {
  logoDataUrl?: string | null;
  /** The invoice number this payment was applied to (resolved by the caller). */
  invoiceNo?: string;
}

export function buildReceiptPdf(p: Payment, opt: ReceiptPdfOptions = {}): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 16; const right = 210 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  // ---- letterhead ----
  if (opt.logoDataUrl) {
    try { pdf.addImage(opt.logoDataUrl, 'PNG', MX, y - 2, 18, 18); } catch { /* ignore */ }
  }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text(SCHOOL.name, MX + 22, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`, MX + 22, y + 9);
  y += 22;
  setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 8;

  // ---- title + meta ----
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text('RECEIPT', MX, y);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
  if (p.receiptNo) pdf.text(`No. ${p.receiptNo}`, right, y - 3.5, { align: 'right' });
  pdf.text(`Date ${(p.receivedAt || p.createdAt || '').slice(0, 10)}`, right, y + 0.5, { align: 'right' });
  y += 10;

  // ---- received from ----
  pdf.setFontSize(7.5); setT(MUTED); pdf.text('RECEIVED FROM', MX, y);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); setT(INK);
  pdf.text(p.studentName || p.studentId, MX, y + 5);
  y += 12;

  // ---- amount box (the headline) ----
  setF([240, 244, 250]); setD(NAVY); pdf.setLineWidth(0.5);
  pdf.rect(MX, y, right - MX, 15, 'FD');
  setT(MUTED); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8);
  pdf.text('AMOUNT RECEIVED', MX + 3, y + 5.5);
  setT(NAVY); pdf.setFontSize(16);
  pdf.text(fmtPula(p.amount), right - 3, y + 10.5, { align: 'right' });
  y += 23;

  // ---- detail rows ----
  const rows: Array<[string, string]> = [
    ['Payment method', PAYMENT_METHOD_LABELS[p.method] ?? p.method],
    ['Applied to invoice', opt.invoiceNo || p.invoiceId || '—'],
  ];
  if (p.reference) rows.push(['Reference', p.reference]);
  if (p.recordedBy) rows.push(['Recorded by', p.recordedBy]);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9.5);
  for (const [k, v] of rows) {
    setT(MUTED); pdf.text(k, MX, y);
    setT(INK); pdf.text(pdf.splitTextToSize(String(v), right - MX - 50)[0] as string, MX + 50, y);
    y += 6.5;
  }

  if (p.note) {
    y += 2;
    setT(MUTED); pdf.setFontSize(8.5); pdf.text('Note', MX, y);
    setT(INK); pdf.text(pdf.splitTextToSize(p.note, right - MX - 50) as string[], MX + 50, y);
    y += 8;
  }

  // ---- thanks + signature ----
  y += 6;
  setD(RULE); pdf.setLineWidth(0.3); pdf.line(MX, y, right, y); y += 6;
  setT(INK); pdf.setFont('helvetica', 'italic'); pdf.setFontSize(9.5);
  pdf.text('Received with thanks.', MX, y);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(SCHOOL.motto || '', right, y, { align: 'right' });
  y += 18;
  setD(RULE); pdf.line(MX, y, MX + 60, y);
  setT(MUTED); pdf.setFontSize(8); pdf.text('Authorised signature', MX, y + 4);

  return pdf;
}
